import React, { useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import helperTextError from '@/components/mui/Form/TextField/helperTextError';
import EmailTag, { type DialogUser } from '@/pages/common/Dialog/components/EmailTag';
import UserOptionLabel from '@/pages/common/Dialog/components/UserOptionLabel';

const EMAILS_LIMIT = 12;

interface EmailAutocompleteProps {
  id: string;
  label: string;
  emailOptions: string[];
  users: Record<string, DialogUser>;
  value: string[];
  onChange: (emails: string[]) => void;
  onBlur: () => void;
  errorMessage?: string;
  required?: boolean;
  autoFocus?: boolean;
}

// Addresses are trimmed where they become a chip, never on a keystroke: the chip
// then shows exactly what gets submitted, and a stray space can no longer make
// "a@b.co" and " a@b.co" two different recipients — which slipped a duplicate
// past both the option filter and the dedupe in the submit handler.
const toChips = (emails: readonly string[]) => emails.map((email) => email.trim()).filter(Boolean);

const EmailAutocomplete: React.FC<EmailAutocompleteProps> = ({
  id,
  label,
  emailOptions,
  users,
  value,
  onChange,
  onBlur,
  errorMessage,
  required,
  autoFocus,
}) => {
  const [inputValue, setInputValue] = useState('');
  const canAddMore = value.length + 1 <= EMAILS_LIMIT;

  return (
    <Autocomplete<string, true, false, true>
      multiple
      freeSolo
      autoHighlight
      fullWidth
      id={id}
      value={value}
      onChange={(event, newValue) => onChange(toChips(newValue))}
      inputValue={inputValue}
      onBlur={() => {
        onBlur();
        // MUI turns the pending text into a chip on Enter only, so a blur has to
        // commit it too or the address the user just typed is silently dropped.
        if (inputValue) {
          onChange(toChips([...value, inputValue]));
          setInputValue('');
        }
      }}
      onInputChange={(event, newValue) => {
        if (canAddMore) setInputValue(newValue);
      }}
      // No `filterSelectedOptions`: it hides only what THIS field has picked,
      // while the caller already subtracts both fields' picks from
      // `emailOptions` so the two recipient lists never offer the same address.
      options={canAddMore ? emailOptions : []}
      renderOption={({ key, ...optionProps }, tag) => (
        <li key={key} {...optionProps}>
          <UserOptionLabel tag={tag} users={users} />
        </li>
      )}
      getOptionLabel={(option) => (users[option] ? `${users[option].username} ${users[option].userId}` : option)}
      renderValue={(values, getItemProps) =>
        values.map((tag, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return <EmailTag key={key} tag={tag} users={users} {...itemProps} />;
        })
      }
      renderInput={({ inputProps, InputLabelProps, InputProps, ...params }) => (
        <TextField
          {...params}
          // Chrome ignores the `autocomplete="off"` MUI sets and, spotting "email" in
          // the ids, offers saved addresses and fills To and Cc together. An
          // unrecognised token is the value it honours.
          slotProps={{
            htmlInput: { ...inputProps, autoComplete: 'no' },
            input: InputProps,
            inputLabel: InputLabelProps,
          }}
          label={label}
          // Autocomplete drops every prop it does not recognise onto its root
          // <div>, so these three were inert until they were put on the input.
          placeholder={label}
          required={required}
          autoFocus={autoFocus}
          error={Boolean(errorMessage)}
          helperText={helperTextError(errorMessage)}
        />
      )}
    />
  );
};

export default EmailAutocomplete;
